import type { GetStaticProps, NextPage } from "next";
import type { ReactElement, ReactNode } from "react";

import JsonLdManager from "@/components/JsonLdManager/JsonLdManager";
import MaticPageMeta from "@/components/PageMetaManager/MaticPageMeta";
import RedesignLayout from "@/chrome/RedesignLayout";
import {
  MATIC_BAG_PASS_PRODUCT_ID,
  MATIC_BUILDER_KIT_PRODUCT_ID,
  MATIC_CARE_PRODUCT_ID,
  MATIC_PRODUCT_ID,
  MATIC_SERENE_PRODUCT_ID,
  PET_BRUSH_ROLL_PRODUCT_ID,
} from "@/consts/shopify";
import type { Product } from "@/graphql/storefront/generated";
import type { ProductPageProps } from "@/types/pages";
import {
  getShopPayInstallmentsTerms,
  type ShopPayInstallmentsTerms,
} from "@/lib/shopPayInstallments";
import { getProductById } from "@/utils/queries";
import ShopView from "@/views/shop/ShopView";
import { PINNED_BAR_CLEARANCE } from "@/views/shop/sections/configurator/Checkout";
import {
  buildProductStructuredData,
  buildShopFaqStructuredData,
} from "@/views/shop/shopStructuredData";
import DEFAULT_CONFIG from "@/components/PageMetaManager/config";
import {
  BREADCRUMB_META_DATA,
  ORGANIZATION_JSON_LD,
} from "@/components/JsonLdManager/jsonLdContent";

type ProductProps = Pick<
  ProductPageProps,
  | "maticProduct"
  | "maticSereneProduct"
  | "annualBagPassProduct"
  | "maticCareProduct"
  | "builderKitProduct"
  | "petBrushRollProduct"
> & { maticInstallments: ShopPayInstallmentsTerms | null };

/** The Matic's handle in the Shopify store, whose theme page carries the
 *  installments plan data. */
const MATIC_PRODUCT_HANDLE = "matic";

const Product: NextPage<ProductProps> & {
  getLayout?: (page: ReactElement) => ReactNode;
} = ({
  maticProduct,
  maticSereneProduct,
  annualBagPassProduct,
  maticCareProduct,
  builderKitProduct,
  petBrushRollProduct,
  maticInstallments,
}) => {
  const structuredData = [
    ...(maticProduct ? [buildProductStructuredData(maticProduct)] : []),
    buildShopFaqStructuredData(),
  ];

  return (
    <>
      <MaticPageMeta page="product" config={DEFAULT_CONFIG} />
      <JsonLdManager
        breadcrumbs={BREADCRUMB_META_DATA.product}
        homeConfig={ORGANIZATION_JSON_LD}
        structuredData={structuredData}
      />
      <ShopView
        maticProduct={maticProduct}
        maticSereneProduct={maticSereneProduct}
        annualBagPassProduct={annualBagPassProduct}
        maticCareProduct={maticCareProduct}
        builderKitProduct={builderKitProduct}
        petBrushRollProduct={petBrushRollProduct}
        maticInstallments={maticInstallments}
      />
    </>
  );
};

Product.getLayout = (page: ReactElement): ReactNode => (
  <RedesignLayout bottomClearance={PINNED_BAR_CLEARANCE} navScrollsAway>
    {page}
  </RedesignLayout>
);

// Every add-on is fetched independently so one failed product only hides its
// own card instead of taking the whole page's configurator down with it.
const getOptionalProduct = async (
  productId: string,
  label: string,
): Promise<Product | null> =>
  getProductById(productId).catch((error) => {
    console.error(`Failed to load ${label} product`, error);
    return null;
  });

export const getStaticProps: GetStaticProps<ProductProps> = async () => {
  const [
    maticProduct,
    maticSereneProduct,
    annualBagPassProduct,
    maticCareProduct,
    builderKitProduct,
    petBrushRollProduct,
    maticInstallments,
  ] = await Promise.all([
    getProductById(MATIC_PRODUCT_ID),
    getOptionalProduct(MATIC_SERENE_PRODUCT_ID, "Matic Serene"),
    getOptionalProduct(MATIC_BAG_PASS_PRODUCT_ID, "Matic bag pass"),
    getOptionalProduct(MATIC_CARE_PRODUCT_ID, "Matic Care"),
    getOptionalProduct(MATIC_BUILDER_KIT_PRODUCT_ID, "Builder Kit"),
    getOptionalProduct(PET_BRUSH_ROLL_PRODUCT_ID, "Pet Brush Roll"),
    getShopPayInstallmentsTerms(MATIC_PRODUCT_HANDLE),
  ]);

  return {
    props: {
      maticProduct,
      maticSereneProduct,
      annualBagPassProduct,
      maticCareProduct,
      builderKitProduct,
      petBrushRollProduct,
      maticInstallments,
    },
  };
};

export default Product;
